import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { fetchEnums, fetchAttributeSets } from "../../services/meta";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";

type EnumItem = { value: string; labels: { tr: string; en: string } };
type EnumsState = Record<string, { items: EnumItem[] }>;

type AttributeSet = { id: string; match: string[]; fields: string[] };
type AttrSetsState = { default: string[]; sets: AttributeSet[] };

const AttributesAdmin: React.FC = () => {
  const [enums, setEnums] = React.useState<EnumsState>({});
  const [attrSets, setAttrSets] = React.useState<AttrSetsState>({
    default: [],
    sets: [],
  });
  const [enumKey, setEnumKey] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState(
    "chore(meta): update enums/attribute sets"
  );
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const e = await fetchEnums();
        setEnums(e as any);
        const firstKey = Object.keys(e || {})[0] || "";
        setEnumKey(firstKey);
      } catch (_) {}
      try {
        const a = await fetchAttributeSets();
        setAttrSets(a as any);
      } catch (_) {}
    })();
  }, []);

  const addEnumItem = () => {
    if (!enumKey) return;
    setEnums((prev) => {
      const clone = { ...(prev || {}) } as EnumsState;
      const items = clone[enumKey]?.items ? [...clone[enumKey].items] : [];
      items.push({ value: "", labels: { tr: "", en: "" } });
      clone[enumKey] = { items };
      return clone;
    });
  };

  const removeEnumItem = (idx: number) => {
    if (!enumKey) return;
    setEnums((prev) => {
      const clone = { ...(prev || {}) } as EnumsState;
      const items = (clone[enumKey]?.items || []).filter((_, i) => i !== idx);
      clone[enumKey] = { items };
      return clone;
    });
  };

  const saveAll = async () => {
    try {
      setLoading(true);
      setError("");
      const branch =
        publishMode === "preview"
          ? branchName || `preview/meta-${Date.now()}`
          : undefined;
      await contentWrite(
        "data/meta/enums.json",
        JSON.stringify(enums, null, 2),
        `${commitMsg} (enums)`,
        branch
      );
      await contentWrite(
        "data/meta/attribute-sets.json",
        JSON.stringify(attrSets, null, 2),
        `${commitMsg} (attribute-sets)`,
        branch
      );
      if (branch) {
        const pr = await createPreviewPR(
          branch,
          "Meta Update",
          "Enums & Attribute Sets update"
        );
        setPrUrl(pr.url);
      }
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Öznitelikler & Listeler
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: Soldan enum listesini seçip değerleri TR/EN etiketleriyle
          güncelleyin. Sağda Attribute Sets ile sayfada hangi alanların
          gösterileceğini tanımlayın. Yayın çubuğundan Prod veya Preview PR
          seçebilirsiniz.
        </Alert>
        <Grid container spacing={3}>
          {/* Enums */}
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Enums
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <TextField
                    label="Enum"
                    select
                    SelectProps={{ native: true }}
                    value={enumKey}
                    onChange={(e) => setEnumKey(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {Object.keys(enums || {}).map((key) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={addEnumItem}>
                    Eleman Ekle
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {(enums?.[enumKey]?.items || []).map((it, idx) => (
                    <Grid
                      container
                      spacing={1}
                      key={`${enumKey}-${idx}`}
                      alignItems="center"
                    >
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Değer"
                          value={it.value}
                          onChange={(e) =>
                            setEnums((prev) => {
                              const clone = { ...(prev || {}) } as EnumsState;
                              const items = [...(clone[enumKey]?.items || [])];
                              items[idx] = {
                                ...items[idx],
                                value: e.target.value,
                              } as any;
                              clone[enumKey] = { items };
                              return clone;
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Label (TR)"
                          value={it.labels?.tr || ""}
                          onChange={(e) =>
                            setEnums((prev) => {
                              const clone = { ...(prev || {}) } as EnumsState;
                              const items = [...(clone[enumKey]?.items || [])];
                              const labels = {
                                ...(items[idx].labels || {}),
                                tr: e.target.value,
                              } as any;
                              items[idx] = { ...items[idx], labels } as any;
                              clone[enumKey] = { items };
                              return clone;
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Label (EN)"
                          value={it.labels?.en || ""}
                          onChange={(e) =>
                            setEnums((prev) => {
                              const clone = { ...(prev || {}) } as EnumsState;
                              const items = [...(clone[enumKey]?.items || [])];
                              const labels = {
                                ...(items[idx].labels || {}),
                                en: e.target.value,
                              } as any;
                              items[idx] = { ...items[idx], labels } as any;
                              clone[enumKey] = { items };
                              return clone;
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <Button
                          color="error"
                          onClick={() => removeEnumItem(idx)}
                        >
                          Sil
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Attribute Sets */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Attribute Sets
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Default Alanlar (virgül ile)"
                    value={(attrSets?.default || []).join(", ")}
                    onChange={(e) =>
                      setAttrSets((prev) => ({
                        ...(prev || { default: [], sets: [] }),
                        default: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }))
                    }
                  />

                  {(attrSets?.sets || []).map((s, idx) => (
                    <Card key={s.id} variant="outlined">
                      <CardContent>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="ID"
                              value={s.id}
                              onChange={(e) =>
                                setAttrSets((prev) => {
                                  const clone = {
                                    ...(prev || { default: [], sets: [] }),
                                  } as AttrSetsState;
                                  const sets = [...(clone.sets || [])];
                                  sets[idx] = {
                                    ...sets[idx],
                                    id: e.target.value,
                                  };
                                  clone.sets = sets;
                                  return clone;
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              label="Match (virgül ile)"
                              value={(s.match || []).join(", ")}
                              onChange={(e) =>
                                setAttrSets((prev) => {
                                  const clone = {
                                    ...(prev || { default: [], sets: [] }),
                                  } as AttrSetsState;
                                  const sets = [...(clone.sets || [])];
                                  sets[idx] = {
                                    ...sets[idx],
                                    match: e.target.value
                                      .split(",")
                                      .map((m) => m.trim())
                                      .filter(Boolean),
                                  };
                                  clone.sets = sets;
                                  return clone;
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Fields (virgül ile)"
                              value={(s.fields || []).join(", ")}
                              onChange={(e) =>
                                setAttrSets((prev) => {
                                  const clone = {
                                    ...(prev || { default: [], sets: [] }),
                                  } as AttrSetsState;
                                  const sets = [...(clone.sets || [])];
                                  sets[idx] = {
                                    ...sets[idx],
                                    fields: e.target.value
                                      .split(",")
                                      .map((m) => m.trim())
                                      .filter(Boolean),
                                  };
                                  clone.sets = sets;
                                  return clone;
                                })
                              }
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Publish Bar */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mt: 3 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/meta`}
          />
          <Button variant="contained" onClick={saveAll} disabled={loading}>
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={3000}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AttributesAdmin;
